/** Shared scoped catalog read; optional capabilities support mixed Web/Shopify releases. */
import { useQuery } from '@tanstack/react-query'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { buildShopifyAppScopeKey, shopifyQueryKey, useShopifyAppContext } from '../../ShopifyAppContext'
import { shopifyApiFetch, type ShopifyTokenSource } from '../../shopifyApiFetch'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { integrationsPayloadSchema } from './integration-page-model'

export async function integrationResponse(response: Pick<Response, 'ok' | 'json'>) {
  const value: unknown = await response.json()
  if (!response.ok) {
    const error = value as { error?: string }
    throw new Error(error.error ?? shopifyComponentCopy.integrationsPage.propertiesFailed)
  }
  return value
}

export function useIntegrationCatalog(shopify: ShopifyTokenSource, connected: boolean) {
  const scope = useShopifyAppContext()
  const scopeKey = buildShopifyAppScopeKey(scope)
  const active = () => !scope.isActiveScope || scope.isActiveScope(scopeKey)
  return useQuery({
    queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['testfeed-integrations']),
    enabled: connected && !!scope.orgId && !!scope.connectionId && active(),
    queryFn: async ({ signal }) => {
      const result = integrationsPayloadSchema.parse(
        await integrationResponse(
          await shopifyApiFetch(shopify, '/api/testfeed-integrations', {
            onPhase: () => {
              if (!active()) throw Error('SHOPIFY_INTEGRATIONS_SCOPE_CHANGED')
            },
            init: { signal },
          }),
        ),
      )
      if (
        !active() ||
        (result.capabilities?.length &&
          (result.organizationId !== scope.orgId || result.storeConnectionId !== scope.connectionId))
      )
        throw Error('SHOPIFY_INTEGRATIONS_SCOPE_CHANGED')
      return result
    },
    ...shopifyQueryPolicies.integrations,
  })
}
